import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';

import { storableError } from '../util/errors';
import { selectProfileListing } from '../containers/NewListingPage/NewListingPage.duck';

/**
 * Global onboarding-stepper slice.
 *
 * Holds just enough of the provider's profile listing for
 * util/onboardingStages.js to compute the 5-stage progress: the id/state
 * (which listing, how far along) plus the extended data the completeness
 * checklist reads. Which listing wins a tie is NOT reimplemented here —
 * selectProfileListing (NewListingPage.duck.js) is the one answer to "which
 * listing is the profile", reused verbatim.
 *
 * Food companies have no listing, so this slice stays at its initial (null)
 * shape for them; getOnboardingStage reads the user entity directly in that
 * case and never needs profileListingId.
 */

const ONBOARDING_LISTING_QUERY_PARAMS = {
  page: 1,
  perPage: 100,
  'fields.ownListing': ['state', 'title', 'createdAt', 'deleted', 'publicData', 'privateData'],
  // The cover image is part of "registration complete" (onboardingStages.js
  // lists a missing one on the checklist), and it is a relationship, not an
  // attribute, so it has to be asked for. Only the ids are kept.
  include: ['images'],
};

// ================ Async Thunks ================ //

const queryOnboardingListingPayloadCreator = (_, { extra: sdk, rejectWithValue }) =>
  sdk.ownListings
    .query(ONBOARDING_LISTING_QUERY_PARAMS)
    .then(response => {
      const listings = response.data.data || [];
      const profileListing = selectProfileListing(listings);

      const imageRefs = profileListing?.relationships?.images?.data || [];

      return {
        profileListingId: profileListing?.id?.uuid || null,
        profileListingState: profileListing?.attributes?.state || null,
        listingPublicData: profileListing?.attributes?.publicData || null,
        listingPrivateData: profileListing?.attributes?.privateData || null,
        listingImageIds: imageRefs.map(ref => ref?.id?.uuid).filter(Boolean),
      };
    })
    .catch(e => rejectWithValue(storableError(e)));

export const queryOnboardingListingThunk = createAsyncThunk(
  'onboarding/queryOnboardingListing',
  queryOnboardingListingPayloadCreator,
  {
    // Both the Topbar's fetchCurrentUser hook (user.duck.js) and this page's
    // own loadData can ask for the same query in the same tick — only one
    // request should ever be in flight.
    condition: (_, { getState }) => !getState().onboarding?.queryInProgress,
  }
);

export const queryOnboardingListing = () => dispatch =>
  dispatch(queryOnboardingListingThunk()).unwrap();

// ================ Slice ================ //

const initialState = {
  queryInProgress: false,
  queryResolved: false,
  queryError: null,
  profileListingId: null,
  profileListingState: null,
  listingPublicData: null,
  listingPrivateData: null,
  listingImageIds: [],
};

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    // Listing writes elsewhere (EditListingPage.duck.js: update, publish_draft)
    // change what the stepper should show. Resetting queryResolved to false is
    // enough — the next fetchCurrentUser resolution re-dispatches the query,
    // the same eventually-consistent pattern currentUserHasListings already uses.
    invalidateOnboarding: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(queryOnboardingListingThunk.pending, state => {
        state.queryInProgress = true;
        state.queryResolved = false;
        state.queryError = null;
      })
      .addCase(queryOnboardingListingThunk.fulfilled, (state, action) => {
        state.queryInProgress = false;
        state.queryResolved = true;
        state.profileListingId = action.payload.profileListingId;
        state.profileListingState = action.payload.profileListingState;
        state.listingPublicData = action.payload.listingPublicData;
        state.listingPrivateData = action.payload.listingPrivateData;
        state.listingImageIds = action.payload.listingImageIds || [];
      })
      .addCase(queryOnboardingListingThunk.rejected, (state, action) => {
        state.queryInProgress = false;
        state.queryResolved = true;
        state.queryError = action.payload;
      });
  },
});

export const { invalidateOnboarding } = onboardingSlice.actions;

export default onboardingSlice.reducer;

// ================ Selectors ================ //

// Memoized so it returns the SAME object reference across renders when the
// underlying primitives haven't changed — Topbar and EmailVerificationPage
// both call this on every store update, and an unmemoized object literal here
// makes useSelector see "always changed" and rerender on every dispatch.
export const selectOnboardingListing = createSelector(
  [
    state => state.onboarding.profileListingId,
    state => state.onboarding.profileListingState,
    state => state.onboarding.listingPublicData,
    state => state.onboarding.listingPrivateData,
    state => state.onboarding.listingImageIds,
  ],
  (profileListingId, profileListingState, listingPublicData, listingPrivateData, listingImageIds) =>
    profileListingId
      ? {
          id: { uuid: profileListingId },
          attributes: {
            state: profileListingState,
            publicData: listingPublicData,
            privateData: listingPrivateData,
          },
          // Same shape as a denormalised listing's `images`, which is what
          // EditListingWizard's tabCompleted reads for the Photos tab too.
          images: (listingImageIds || []).map(uuid => ({ id: { uuid }, type: 'image' })),
        }
      : null
);
